// Table for Displaying Tickets Associated with an Asset inside of a Modal Tab

import {
    Link,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";

import { WpGenericPost, WpTicketMetaBox } from "@hooks/use-get/tickets/types";
import { formatDate } from "@utils/format-utils";
import { useCallback, useEffect, useState } from "react";
import { useGetTickets } from "@hooks/use-get/tickets";
import { Loader } from "@components/loader";

type AssetTicketEntry = {
    id: {
        ID: number;
        post_author: string;
        post_date: string;
        post_date_gmt: string;
        post_content: string;
        post_title: string;
        post_excerpt: string;
        post_status: string;
        comment_status: string;
        ping_status: string;
        post_password: string;
        post_name: string;
        to_ping: string;
        pinged: string;
        post_modified: string;
        post_modified_gmt: string;
        post_content_filtered: string;
        post_parent: number;
        guid: string;
        menu_order: number;
        post_type: string;
        post_mime_type: string;
        comment_count: string;
        filter: string;
        to: string;
    },
    ticket_number: string;
    status_ticket: boolean;
}

type AssetTicketsTabTableProps = { entries: AssetTicketEntry[] };

const isValidTicket = (
    ticket: WpGenericPost<WpTicketMetaBox> | null
): ticket is WpGenericPost<WpTicketMetaBox> => {
    return Boolean(
        ticket &&
        ticket.title &&
        typeof ticket.title.rendered === "string"
    );
};

export const AssetTicketsTabTable = ({ entries }: AssetTicketsTabTableProps) => {
    const [tickets, setTickets] = useState<WpGenericPost<WpTicketMetaBox>[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const { getOneTicket } = useGetTickets()

    const fetchTickets = useCallback(async (data: any[]) => {
        setLoading(true);

        try {
            const ticketIds = data
                .map((entry) => Number(entry.id.ID))
                .filter((id) => Number.isFinite(id) && id > 0);

            if (ticketIds.length === 0) {
                setTickets([]);
                return;
            }

            const fetchedTickets = await Promise.all(
                ticketIds.map((id) => getOneTicket(id)),
            );

            const safeTickets = fetchedTickets
                .filter(isValidTicket)
                .sort(
                    (a, b) =>
                        Number(b.title.rendered) - Number(a.title.rendered)
                );

            setTickets(safeTickets);
        } finally {
            setLoading(false);
        }
    }, [getOneTicket]);

    useEffect(() => {
        if (entries.length > 0) {
            fetchTickets(entries);
        } else {
            setTickets([]);
        }
    }, [entries, fetchTickets]);

    const createModalHref = (id: number) => {
        const splitUrl = window.location.href.split("/");
        return `${splitUrl[0]}//${splitUrl[2]}/tickets/${id}`;
    };

    if (!loading && entries.length === 0) {
        return (
            <Typography variant="body1" sx={{ justifyContent: "center", display: "flex", my: 2 }}>
                This asset has not been associated with any tickets.
            </Typography>
        )
    }

    return (
        <>
            {loading && (<Loader message="loading tickets..." />)}
            {!loading && tickets.length > 0 && (
                <TableContainer>
                    <Table aria-label="asset tickets table">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    Ticket Number
                                </TableCell>
                                <TableCell>
                                    Status
                                </TableCell>
                                <TableCell>
                                    Date Issued
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tickets.map((ticket: WpGenericPost<WpTicketMetaBox>, idx: number) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        <Link href={createModalHref(ticket.id)} color="primary">
                                            {ticket.title.rendered}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        {ticket.meta_box.status_ticket}
                                    </TableCell>
                                    <TableCell>
                                        {formatDate(ticket.date)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    )
};